import React from 'react';
import PropTypes from 'prop-types';

import { AppLink, routeNaming } from '../../routes';
import { AddToCartSection } from '../../common/add-to-cart-section';
import { enforceParameters } from '../../hocs/enforce-parameters';
import { IntranetProductCard } from '../intranet-product-card';
import { ProductImage } from '../product-image';
import { IntranetLink } from '../intranet-link';
import { CompareCheckbox } from '../compare-checkbox/CompareCheckbox';
import { ConfigParametersStore } from '../../stores/ConfigParametersStore';
import styles from './ProductCard.css';

const IPropTypes = {
  className: PropTypes.string,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    ableToSell: PropTypes.bool.isRequired,
    shouldShowPrice: PropTypes.func.isRequired,
    brand: PropTypes.string.isRequired,
    characteristics: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string.isRequired,
    })).isRequired,
    financing: PropTypes.shape({
      priceString: PropTypes.string,
      zeroDayExpiry: PropTypes.arrayOf(PropTypes.shape({
        amount: PropTypes.number.isRequired,
        priceString: PropTypes.string.isRequired,
      })).isRequired,
    }).isRequired,
    coverImage: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    labelColor: PropTypes.string,
    stockIsAvailable: PropTypes.func,
    stockIsAlmostNone: PropTypes.func,
    stockIsEmpty: PropTypes.func,
    stockIsNearlyEmpty: PropTypes.func,
    inUse: PropTypes.bool,
    inWeb: PropTypes.bool,
    specificId: PropTypes.number,
  }).isRequired,
};

const defaultProps = {
  className: '',
};

const renderCharacteristics = characteristics => (
  <ul className={styles.charList}>
    {
      characteristics.map(char => (
        <li
          className={styles.characteristic}
          key={char.text}
        >
          {char.text}
        </li>
      ))
    }
  </ul>
);

const renderImage = (id, coverImage, showNormalLink, specificId) => {
  const productImage = (
    <ProductImage
      className={styles.image}
      src={coverImage}
      size="lg"
    />
  );
  if (!showNormalLink) {
    return (
      <IntranetLink
        className={styles.imageLink}
        productId={id}
        specificId={specificId}
      >
        {productImage}
      </IntranetLink>
    );
  }
  return (
    <AppLink
      routeName={routeNaming.PRODUCT_DETAIL}
      pathParams={{ id }}
      className={styles.imageLink}
    >
      {productImage}
    </AppLink>
  );
};

const renderTitle = (id, name, showNormalLink, specificId) => {
  const title = (
    <h2 className={styles.name}>
      {name}
    </h2>
  );
  if (!showNormalLink) {
    return (
      <IntranetLink
        className={styles.imageLink}
        productId={id}
        specificId={specificId}
      >
        {title}
      </IntranetLink>
    );
  }
  return (
    <AppLink
      routeName={routeNaming.PRODUCT_DETAIL}
      pathParams={{ id }}
      className={styles.imageLink}
    >
      {title}
    </AppLink>
  );
};

const renderStock = (product) => {
  if (product.isService) {
    return null;
  }
  if (product.stockIsEmpty()) {
    const piece = product.notAbleToSellString && product.notAbleToSellString.split(' disponible ');
    let message = `Sin stock. Fecha estimada: ${piece && piece.length > 1 ? piece[1] : '-'}.`;
    if (!product.shouldShowPrice()) {
      message += 'Fijado el precio se publicará inmediatamente.';
    }
    return (
      <p className={styles.withoutStockText}>
        {message}
      </p>
    );
  }
  if (product.stockIsAlmostNone()) {
    return <p className={styles.stockText}> No hay, o hay muy poco </p>;
  }
  if (product.stockIsNearlyEmpty()) {
    return <p className={styles.stockText}> Stock disponible, últimas unidades </p>;
  }
  return <p className={styles.stockText}> Stock disponible </p>;
};

const renderButton = product => (
  <AddToCartSection
    product={product}
    showText={false}
  />
);

const renderContent = (className, product, financingWithMaxAmount) => {
  const showNormalLink = true;
  return (
    <article className={[className, styles.container].join(' ')}>
      {
        !!product.label &&
        <span
          className={styles.productLabel}
          style={{
            backgroundColor: product.labelColor,
          }}
        >
          {product.label}
        </span>
      }
      {
        renderImage(
          product.id,
          product.coverImage,
          showNormalLink,
          product.specificId,
        )
      }
      <span className={styles.brand}>{product.brand}</span>
      {
        renderTitle(
          product.id,
          product.name,
          showNormalLink,
          product.specificId,
        )
      }
      {renderCharacteristics(product.characteristics)}
      <AppLink
        routeName={routeNaming.PRODUCT_DETAIL}
        pathParams={{ id: product.id }}
        className={styles.infoLink}
      >
        Ver información del producto
      </AppLink>
      <div className={styles.footerContainer}>
        <div>
          <CompareCheckbox product={product} />
          {
            product.shouldShowPrice() ?
              <p className={styles.price}>${product.financing.priceString}</p> :
              <p className={styles.financing}>Precio sin determinar</p>
          }
        </div>
        <div className={styles.buttonContainer}>
          {
            product.shouldShowPrice() && financingWithMaxAmount &&
            <p className={styles.financing}>
              ${financingWithMaxAmount.priceString} x {financingWithMaxAmount.amount} cuotas
            </p>
          }
          {
            product.inUse ?
              renderStock(product) :
              <p className={styles.productInUseText}> Fuera de uso </p>
          }
          {renderButton(product)}
        </div>
      </div>
    </article>
  );
};

const ProductCard = (props) => {
  // If the user is navigating the intranet, then render the special product
  // card.
  // NOTE: this check is done inside this component to make it transparent
  // to all components that want to render a product card.
  if (ConfigParametersStore.showIntranet) {
    return (
      <IntranetProductCard {...props} />
    );
  }
  const { product: { financing: { zeroDayExpiry } } } = props;
  const financingWithMaxAmount = !!zeroDayExpiry.length && zeroDayExpiry.reduce((a, b) => (
    (a.amount > b.amount)
      ? a
      : b
  ));
  return renderContent(props.className, props.product, financingWithMaxAmount);
};

ProductCard.propTypes = IPropTypes;
ProductCard.defaultProps = defaultProps;

const WrappedProductCard = enforceParameters(ProductCard);

export { WrappedProductCard as ProductCard };



// WEBPACK FOOTER //
// ./src/common/product-card/ProductCard.jsx